/**
 * Komponent renderujący toolbar nad tabelą.
 * Dane z kontekstu WiseTable (useWiseTable).
 */

import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, FilterCircle, Gear } from 'react-bootstrap-icons';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { Button as ButtonMui } from '@mui/material';
import { useAppTheme } from 'theme/useAppTheme';

import { axios } from 'api';
import { useNotifications } from 'hooks';
import { useWiseTable } from 'components/wise/WiseTable/context';
import TableParameters from 'components/wise/WiseTable/components/TableParameters';
import AdvancedFiltersDialog from 'components/wise/WiseTable/components/AdvancedFiltersDialog';
import FiltersActionPopper from 'components/wise/WiseTable/components/FiltersActionPopper';
import {
  buildCsv,
  buildCsvFileName,
  downloadCsv
} from 'components/wise/WiseTable/utils/exportToCsv';

import styles from 'theme/components/wise/WiseTable/components/TableTopToolbar/TableTopToolbar.module.scss';

const TableTopToolbar: FC = () => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const { showErrorMessage } = useNotifications();
  const [
    filtersActionPopperAnchor,
    setFiltersActionPopperAnchor
  ] = useState<HTMLElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const {
    config,
    queryParams,
    updateParameter,
    activeParameters,
    handleClearParameters,
    advancedFilters,
    tableData,
    loadDataUrl,
    effectiveQueryParams
  } = useWiseTable();

  const {
    parameters: parametersConfig,
    columns,
    withAdvancedFilters
  } = config;

  const {
    onOpenAdvancedFiltersDialog,
    onCloseAdvancedFiltersDialog,
    advancedFiltersDialogOpen,
    setTabIndex,
    handleOpenSaveSettingsNameDialog,
    handleClearSavedFilters,
    savedFilters,
    showColumnFilterIcons,
    onToggleColumnFilterIcons
  } = advancedFilters;

  const hasActiveParameters = useMemo(
    () => activeParameters.length > 0,
    [activeParameters]
  );

  const hasActiveFilters = useMemo(() => {
    const parameterFieldSymbols = new Set(parametersConfig.map((p) => p.field_symbol));
    const queryParamKeys = Object.keys(queryParams);
    return queryParamKeys.some(
      (key) =>
        key !== 'page' &&
        key !== 'limit' &&
        !parameterFieldSymbols.has(key) &&
        queryParams[key]
    );
  }, [queryParams, parametersConfig]);

  const hasActiveAdvancedFilters = (savedFilters?.length ?? 0) > 0;
  const showClearButton =
    hasActiveParameters || hasActiveFilters || hasActiveAdvancedFilters;

  // Eksport CSV - czysto frontendowy: powtarzamy ten sam endpoint co tabela,
  // z aktualnymi filtrami + limit=total_count, zeby dostac wszystkie wiersze.
  // Naglowki: bierzemy nazwy i klucze z aktualnie widocznych kolumn (po hidden filter).
  const handleExportCsv = async () => {
    if (isExporting) return;
    if (!loadDataUrl) {
      showErrorMessage(t('Brak danych do eksportu'));
      return;
    }
    const totalCount = tableData?.total_count ?? 0;
    if (totalCount <= 0) {
      showErrorMessage(t('Brak danych do eksportu'));
      return;
    }

    setIsExporting(true);
    try {
      const params = { ...effectiveQueryParams, page: 1, limit: totalCount };
      // axios interceptor zwraca rozpakowane `data` jako wartosc Promise (patrz api/axios.ts),
      // ale typ generika trzyma sygnature AxiosResponse<T>. Bezpieczny fallback na obie konwencje.
      const response = await axios.get<{ items: Record<string, unknown>[] }>(loadDataUrl, { params });
      const payload = ((response as unknown) as { data?: { items?: Record<string, unknown>[] } }).data ?? (response as unknown as { items?: Record<string, unknown>[] });
      const items = payload?.items ?? [];

      // Naglowki = aktualnie skonfigurowane kolumny tabeli (te z `field_symbol`).
      // Mapowanie label / klucz: backend zwraca dane pod kluczami `field_symbol`,
      // wiec uzywamy ich jako klucze pol w wierszach.
      const headers = columns
        .filter((col) => Boolean(col.field_symbol))
        .map((col) => ({
          key: col.field_symbol as string,
          label: col.label || (col.field_symbol as string)
        }));

      const csvHeaders = headers.length > 0
        ? headers
        : (items[0] ? Object.keys(items[0]).map((key) => ({ key, label: key })) : []);

      const csv = buildCsv(items, { headers: csvHeaders });
      const fileName = buildCsvFileName(config.componentSymbol || 'export');
      downloadCsv(csv, fileName);
    } catch {
      showErrorMessage(t('Nie udalo sie wyeksportowac danych do CSV'));
    } finally {
      setIsExporting(false);
    }
  };

  const leftParameters = useMemo(
    () =>
      parametersConfig.filter(
        (param) =>
          param.type === 'checkbox' ||
          param.type === 'toggle' ||
          param.type === 'date_range' ||
          param.type === 'range'
      ),
    [parametersConfig]
  );

  const rightParameters = useMemo(
    () => parametersConfig.filter((param) => param.type === 'search'),
    [parametersConfig]
  );

  const otherParameters = useMemo(
    () =>
      parametersConfig.filter(
        (param) =>
          param.type !== 'checkbox' &&
          param.type !== 'toggle' &&
          param.type !== 'date_range' &&
          param.type !== 'range' &&
          param.type !== 'search'
      ),
    [parametersConfig]
  );

  return (
    <div className={styles.toolbarWrapper}>
      <div className={styles.toolbarLeft}>
        {leftParameters.length > 0 && (
          <TableParameters
            parameters={leftParameters}
            queryParams={queryParams}
            updateParameter={updateParameter}
          />
        )}
        {otherParameters.length > 0 && (
          <TableParameters
            parameters={otherParameters}
            queryParams={queryParams}
            updateParameter={updateParameter}
          />
        )}
      </div>

      <div className={styles.toolbarRight}>
        {withAdvancedFilters && columns.length > 0 && (
          <>
            <button
              type="button"
              className={styles.filterIconButton}
              onClick={(e) => setFiltersActionPopperAnchor(e.currentTarget)}
              aria-label={t('Akcje filtrów')}
              aria-haspopup="true"
              aria-expanded={Boolean(filtersActionPopperAnchor)}>
              <Gear className={styles.filterIcon} size={20} />
            </button>
            <FiltersActionPopper
              open={Boolean(filtersActionPopperAnchor)}
              anchorEl={filtersActionPopperAnchor}
              onClose={() => setFiltersActionPopperAnchor(null)}
              onOpenColumnVisibility={() => {
                setTabIndex(1);
                onOpenAdvancedFiltersDialog?.();
              }}
              onOpenAdvancedFilters={() => {
                setTabIndex(0);
                onOpenAdvancedFiltersDialog?.();
              }}
              onOpenSavedSettings={() => {
                setTabIndex(2);
                onOpenAdvancedFiltersDialog?.();
              }}
              onSaveCurrentLayout={() => {
                setTabIndex(2);
                onOpenAdvancedFiltersDialog?.();
                handleOpenSaveSettingsNameDialog?.();
              }}
              onExportCsv={handleExportCsv}
            />
            <button
              type="button"
              className={styles.filterIconButton}
              onClick={onToggleColumnFilterIcons}
              aria-label={t('Pokaż / Ukryj filtry')}
              title={t('Pokaż / Ukryj filtry')}
              aria-pressed={showColumnFilterIcons}>
              {showColumnFilterIcons ? (
                <FilterCircle
                  className={styles.filterIconActive}
                  size={20}
                />
              ) : (
                <Filter className={styles.filterIcon} size={20} />
              )}
            </button>
          </>
        )}
        {showClearButton && (
          <ButtonMui
            variant="text"
            color="secondary"
            onClick={() => {
              handleClearParameters();
              handleClearSavedFilters?.();
            }}
            startIcon={<FilterListOffIcon />}
            sx={{
              lineHeight: '28px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              color: theme.palette.app.textMutedAlpha,
              '&:hover': {
                color: theme.palette.app.secondaryLight,
                backgroundColor: theme.palette.app.hoverBg
              }
            }}>
            {t('Wyczyść')}
          </ButtonMui>
        )}
        {rightParameters.length > 0 && (
          <TableParameters
            parameters={rightParameters}
            queryParams={queryParams}
            updateParameter={updateParameter}
          />
        )}
      </div>

      {withAdvancedFilters && columns.length > 0 && (
        <AdvancedFiltersDialog
          open={advancedFiltersDialogOpen ?? false}
          onClose={onCloseAdvancedFiltersDialog ?? (() => null)}
          columns={columns}
          {...advancedFilters}
        />
      )}
    </div>
  );
};

export default TableTopToolbar;
